import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import SetPropStatePreview from "../../features/previews/set_prop_state/SetPropStatePreview";
import SetPropStateNodePreview from "../../features/sequence/node_previews/SetPropStateNodePreview";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { Prop } from "../Prop";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";
import {
  compactSearchableStrings,
  getBooleanSearchLabels,
  SearchLookupContext,
} from "./SearchableInteractionValues";

export interface ISetPropState extends Interaction {
  propId: string;
  propState: string;
  waitForCustomEvent: boolean;
}

export const setPropStateInteraction = (): InteractionFactoryEntity => {
  const view = <SetPropStatePreview />;

  const create = (getKeys: (amount: number) => string[]): ISetPropState => {
    return {
      id: GetNewGuid(),
      name: "Set Prop State",
      type: InteractionType.SetPropState,
      propId: "",
      propState: "",
      waitForCustomEvent: false,
      properties: [],
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): ISetPropState => {
    return interaction as ISetPropState;
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
    getAnswerArray: (originalAnswers: IAnswer[], newKeys: string[]) => IAnswer[]
  ): ISetPropState => {
    return {
      ...interaction,
      id: GetNewGuid(),
    } as ISetPropState;
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    take: Take,
    actorPresets: Actor[]
  ): string[] => {
    const converted = interaction as ISetPropState;
    const warnings: string[] = [];

    if (!converted.propState) warnings.push("No Propstate selected");

    return warnings;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[],
    _getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const converted = interaction as ISetPropState;
    const searchableStrings = compactSearchableStrings(
      interaction.name,
      searchLookupContext?.getPropSearchTerms(converted.propId) ??
        converted.propId,
      converted.propState,
      getBooleanSearchLabels(
        converted.waitForCustomEvent,
        "Wait For Custom Event",
        "Do Not Wait For Custom Event",
      ),
    );

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType
    );
  };

  const filterType: string = "SetPropState";

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord
  ): string[] => {
    const converted = interaction as ISetPropState;
    const errors: string[] = [];

    if (!converted.propId) errors.push("No prop selected");

    return errors;
  };

  return {
    View: view,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <SetPropStateNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
